import React, { useState } from 'react'
import styles from './BookingSection.module.css'
import { AppointmentDetails } from '@/types/appointment'

interface Step3SuccessProps {
  appointment: AppointmentDetails
  onBookAnother: () => void
}

export const Step3Success: React.FC<Step3SuccessProps> = ({
  appointment,
  onBookAnother,
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(appointment.appointmentId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className={styles.successCard} id="printable-voucher" role="region" aria-label="Appointment Confirmation">
      {/* Official Voucher Brand Header (Visible on both Screen & Print) */}
      <div className={styles.voucherHeader}>
        <div className={styles.voucherBrand}>
          <img
            src="/assets/lalpathlabs-logo-white.png"
            alt="Dr Lal PathLabs"
            className={styles.voucherLogo}
          />
          <div className={styles.voucherPartner}>
            <span className={styles.voucherTag}>Authorized Collection Center</span>
            <span className={styles.voucherName}>Nextgen Health Care Services</span>
          </div>
        </div>
        <div className={styles.voucherBadge}>
          <span>APPOINTMENT VOUCHER</span>
        </div>
      </div>

      {/* Top Banner with Checkmark */}
      <div className={styles.successHero}>
        <div className={styles.successCheckCircle} aria-hidden="true">
          ✓
        </div>
        <span className={styles.successPreTitle}>BOOKING CONFIRMED</span>
        <h2 className={styles.successHeading}>Appointment Booked Successfully!</h2>
        <p className={styles.successValiditySummary}>
          You have booked your appointment successfully for{' '}
          <strong>{appointment.day}</strong>. Your appointment is valid between{' '}
          <strong>{appointment.validityTime}</strong> on{' '}
          <strong>{appointment.date}</strong>.
        </p>
      </div>

      {/* Reception Appointment ID Box */}
      <div className={styles.idBox}>
        <div className={styles.idBoxContent}>
          <span className={styles.idBoxLabel}>
            Please share below appointment ID at the reception
          </span>
          <div className={styles.idCodeRow}>
            <span className={styles.idCode}>{appointment.appointmentId}</span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopyId}
              aria-label="Copy Appointment ID"
            >
              {copied ? '✓ Copied!' : '📋 Copy ID'}
            </button>
          </div>
        </div>
      </div>

      {/* Appointment Information Details Grid */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Patient Name</span>
          <span className={styles.detailValue}>{appointment.patientName}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Registered Mobile</span>
          <span className={styles.detailValue}>+91 {appointment.mobileNumber}</span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Appointment Day &amp; Date</span>
          <span className={styles.detailValue}>
            {appointment.day}, {appointment.date}
          </span>
        </div>
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Valid Time Window</span>
          <span className={styles.detailValueHighlight}>{appointment.validityTime}</span>
        </div>
        <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
          <span className={styles.detailLabel}>Collection Center &amp; Address</span>
          <span className={styles.detailValue}>
            <strong>{appointment.centerName}</strong>
            <br />
            {appointment.centerAddress}
          </span>
        </div>
      </div>

      {/* Patient Checklist & Guidelines */}
      {appointment.instructions && appointment.instructions.length > 0 && (
        <div className={styles.instructionsContainer}>
          <h4 className={styles.instructionsTitle}>
            <span>📋</span> Important Patient Check-In Instructions
          </h4>
          <ul className={styles.instructionsList}>
            {appointment.instructions.map((item, idx) => (
              <li key={idx} className={styles.instructionItem}>
                <span className={styles.instructionBullet}>•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons (Excluded from print) */}
      <div className={`${styles.successActions} ${styles.noPrint}`}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={handlePrint}
        >
          🖨️ Print / Save Receipt
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onBookAnother}
        >
          Book Another Appointment
        </button>
      </div>

      {/* Disclaimer (Excluded from print) */}
      <p className={`${styles.smsDisclaimer} ${styles.noPrint}`}>
        A confirmation SMS with this Appointment ID and center navigation details has been dispatched to your mobile.
      </p>
    </div>
  )
}
